import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import "@/App.css";
import axios from "axios";
import useSWR from "swr";
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap, ZoomControl } from "react-leaflet";
import L from "leaflet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { MapPin, Instagram, Pencil, Trash2, Share2, LogIn, LogOut, User as UserIcon, HelpCircle, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Search, Bell, Calendar, Clock, Navigation, Eye, EyeOff } from "lucide-react";
import { LoginDialog } from "@/components/LoginDialog";
import { HelpDialog } from "@/components/HelpDialog";
import { PendingApprovalsDialog } from "@/components/PendingApprovalsDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const isDev = process.env.NODE_ENV === "development";

// Weekday labels (0=Sunday .. 6=Saturday) in Spanish
const WEEKDAYS = [
  { value: 0, label: "Domingo" },
  { value: 1, label: "Lunes" },
  { value: 2, label: "Martes" },
  { value: 3, label: "Miércoles" },
  { value: 4, label: "Jueves" },
  { value: 5, label: "Viernes" },
  { value: 6, label: "Sábado" },
];

const weekdayLabel = (n) => (n !== null && n !== undefined ? WEEKDAYS[Number(n)]?.label ?? "" : "");

// Spanish short weekday + month names
const WEEKDAY_SHORT = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const MONTH_SHORT = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

// Parse "YYYY-MM-DD" as local date (avoids UTC off-by-one)
const parseLocalDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

// "2026-09-03" → "Jueves 3 sep"
const formatFriendlyDate = (iso) => {
  const dt = parseLocalDate(iso);
  if (!dt) return iso || "";
  return `${WEEKDAY_SHORT[dt.getDay()]} ${dt.getDate()} ${MONTH_SHORT[dt.getMonth()]}`;
};

// For an array of weekdays (0=Sun..6=Sat), returns the next date (today counts if included).
const nextDateForWeekdays = (weekdays) => {
  if (!Array.isArray(weekdays) || weekdays.length === 0) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let bestDiff = 8;
  for (const wd of weekdays) {
    const w = Number(wd);
    if (!Number.isFinite(w)) continue;
    const diff = (w - today.getDay() + 7) % 7;
    if (diff < bestDiff) bestDiff = diff;
  }
  if (bestDiff === 8) return null;
  const target = new Date(today);
  target.setDate(today.getDate() + bestDiff);
  const y = target.getFullYear();
  const m = String(target.getMonth() + 1).padStart(2, "0");
  const d = String(target.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Normalize a marker's weekdays field: prefer `weekdays`, fall back to single `weekday` for legacy.
const markerWeekdays = (m) => {
  if (Array.isArray(m?.weekdays) && m.weekdays.length > 0) return m.weekdays.map(Number);
  if (m?.weekday !== null && m?.weekday !== undefined) return [Number(m.weekday)];
  return [];
};

// Sortable timestamp for an event marker. Returns Infinity if we can't compute a next date
// (e.g. weekly event without weekdays) so those float to the end of the list.
const eventNextTimestamp = (marker) => {
  const time = marker?.time || "00:00";
  let dateStr = null;
  if (marker?.recurrence === "weekly") {
    dateStr = nextDateForWeekdays(markerWeekdays(marker));
  } else {
    dateStr = marker?.date || null;
  }
  if (!dateStr) return Number.POSITIVE_INFINITY;
  const dt = parseLocalDate(dateStr);
  if (!dt) return Number.POSITIVE_INFINITY;
  const [h, m] = time.split(":").map(Number);
  dt.setHours(Number.isFinite(h) ? h : 0, Number.isFinite(m) ? m : 0, 0, 0);
  return dt.getTime();
};

// Format an array of weekdays like [1,3,5] → "Lunes, Miércoles y Viernes"
const formatWeekdaysLabel = (weekdays) => {
  const list = (weekdays || []).map(Number).filter((n) => Number.isFinite(n));
  if (list.length === 0) return "";
  if (list.length === 7) return "Todos los días";
  const sorted = [...list].sort((a, b) => a - b);
  const names = sorted.map((n) => WEEKDAYS[n]?.label).filter(Boolean);
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} y ${names[1]}`;
  return names.slice(0, -1).join(", ") + " y " + names[names.length - 1];
};

// Known cities in Neuquén / Río Negro region. Any marker is auto-classified to the closest city
// within CITY_MAX_KM. The chip/dropdown only shows cities with at least one marker.
const CITIES = [
  { id: "neuquen", label: "Neuquén", center: [-38.9516, -68.0591] },
  { id: "cipolletti", label: "Cipolletti", center: [-38.9333, -67.9968] },
  { id: "plottier", label: "Plottier", center: [-38.9666, -68.2333] },
  { id: "centenario", label: "Centenario", center: [-38.8272, -68.1300] },
  { id: "cinco-saltos", label: "Cinco Saltos", center: [-38.8253, -68.0619] },
  { id: "allen", label: "Allen", center: [-38.9750, -67.8250] },
  { id: "roca", label: "General Roca", center: [-39.0333, -67.5833] },
  { id: "fernandez-oro", label: "Fernández Oro", center: [-38.9506, -67.9186] },
  { id: "cutral-co", label: "Cutral Có", center: [-38.9333, -69.2333] },
  { id: "plaza-huincul", label: "Plaza Huincul", center: [-38.9333, -69.2166] },
  { id: "zapala", label: "Zapala", center: [-38.8833, -70.1333] },
  { id: "san-martin-andes", label: "San Martín de los Andes", center: [-40.1580, -71.3535] },
  { id: "villa-la-angostura", label: "Villa La Angostura", center: [-40.7549, -71.6494] },
  { id: "junin-andes", label: "Junín de los Andes", center: [-39.9500, -71.0666] },
];

const CITY_INDEX = Object.fromEntries(CITIES.map((c) => [c.id, c]));
const CITY_MAX_KM = 25; // beyond this, classify as "otra"

// Haversine distance in km
const haversine = (a, b) => {
  const R = 6371;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]);
  const dLng = toRad(b[1] - a[1]);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

const cityOfMarker = (marker) => {
  let best = null;
  let bestDist = Infinity;
  for (const c of CITIES) {
    const d = haversine([marker.lat, marker.lng], c.center);
    if (d < bestDist) {
      bestDist = d;
      best = c.id;
    }
  }
  return bestDist <= CITY_MAX_KM ? best : "otra";
};

// Google Maps navigation URL (free, no API key). Opens native app on mobile if installed.
const gmapsDirUrl = (lat, lng) =>
  `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;

// Constants extracted outside component to avoid re-renders
const MAP_CENTER = [-38.9516, -68.0591];
const MAP_ZOOM = 11;
const MAX_MARKERS = 200;
const MAX_PENDING = 20;

// Section themes
const THEMES = {
  schools: {
    primary: "#FF5A5F",
    primaryHover: "#E0484D",
    ring: "ring-[#FF5A5F]/30",
    badge: "bg-[#FF5A5F]",
    tabActive: "bg-[#FF5A5F] text-white",
    tabInactive: "text-slate-600 hover:bg-slate-100",
    label: "Escuelas",
  },
  events: {
    primary: "#8B5CF6",
    primaryHover: "#7C3AED",
    ring: "ring-[#8B5CF6]/30",
    badge: "bg-[#8B5CF6]",
    tabActive: "bg-[#8B5CF6] text-white",
    tabInactive: "text-slate-600 hover:bg-slate-100",
    label: "Eventos",
  },
};

// Axios instance with credentials
axios.defaults.withCredentials = true;

// Attach Bearer token from sessionStorage if present (safer than localStorage; cleared on tab close)
axios.interceptors.request.use((config) => {
  const token = sessionStorage.getItem("sbk_access_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// SWR fetcher with cache
const fetcher = (url) => axios.get(url).then(res => res.data);

// Custom marker icon (color varies by section)
const createCustomIcon = (color = "#FF5A5F") => {
  return L.divIcon({
    className: "custom-marker",
    html: `
      <div style="position: relative;">
        <svg width="32" height="42" viewBox="0 0 32 42" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 0C7.163 0 0 7.163 0 16C0 28 16 42 16 42C16 42 32 28 32 16C32 7.163 24.837 0 16 0Z" fill="${color}"/>
          <circle cx="16" cy="15" r="6" fill="white"/>
        </svg>
      </div>
    `,
    iconSize: [32, 42],
    iconAnchor: [16, 42],
    popupAnchor: [0, -42]
  });
};

// Green marker icon for search results (with pulsing animation)
const createSearchIcon = () => {
  return L.divIcon({
    className: "search-result-marker",
    html: `
      <div class="search-pulse-container">
        <div class="search-pulse-ring"></div>
        <svg width="36" height="48" viewBox="0 0 32 42" fill="none" xmlns="http://www.w3.org/2000/svg" style="position: relative; z-index: 2;">
          <path d="M16 0C7.163 0 0 7.163 0 16C0 28 16 42 16 42C16 42 32 28 32 16C32 7.163 24.837 0 16 0Z" fill="#22C55E"/>
          <circle cx="16" cy="15" r="6" fill="white"/>
        </svg>
      </div>
    `,
    iconSize: [36, 48],
    iconAnchor: [18, 48],
    popupAnchor: [0, -48]
  });
};

function MapClickHandler({ onMapClick, disabled }) {
  useMapEvents({
    dblclick: (e) => {
      if (!disabled) {
        onMapClick(e.latlng);
      }
    },
  });
  return null;
}

function FlyToMarker({ position, zoom = 15 }) {
  const map = useMap();
  
  useEffect(() => {
    if (position) {
      map.flyTo(position, zoom, {
        duration: 1.5
      });
    }
  }, [position, map, zoom]);
  
  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [section, setSection] = useState("schools"); // 'schools' | 'events'
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  const [autoHelpShown, setAutoHelpShown] = useState(false);
  const [showPendingDialog, setShowPendingDialog] = useState(false);
  const [pendingMarkers, setPendingMarkers] = useState([]);
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  // Mobile panel state: 'hidden' | 'collapsed' | 'expanded'
  const [mobilePanelState, setMobilePanelState] = useState('collapsed');
  // Collapsed height: shows only logo + tabs + login button (no cards)
  const MOBILE_COLLAPSED_HEIGHT = 220;
  const [panelHeight, setPanelHeight] = useState(MOBILE_COLLAPSED_HEIGHT);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ y: 0, x: 0, height: MOBILE_COLLAPSED_HEIGHT, time: 0 });

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Touch handlers for mobile panel
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    dragStartRef.current = {
      y: touch.clientY,
      x: touch.clientX,
      height: panelHeight,
      time: Date.now(),
    };
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    const deltaY = dragStartRef.current.y - touch.clientY;
    const deltaX = touch.clientX - dragStartRef.current.x;
    
    // Horizontal swipe detection (more horizontal than vertical)
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 20) {
      return; // Let touchend handle swipe
    }
    
    // Vertical drag - resize panel
    const newHeight = Math.max(80, Math.min(window.innerHeight - 120, dragStartRef.current.height + deltaY));
    setPanelHeight(newHeight);
  };

  const handleTouchEnd = (e) => {
    if (!isDragging) return;
    setIsDragging(false);
    
    const touch = e.changedTouches[0];
    const deltaY = dragStartRef.current.y - touch.clientY;
    const deltaX = touch.clientX - dragStartRef.current.x;
    const deltaTime = Date.now() - dragStartRef.current.time;
    const velocityX = Math.abs(deltaX) / deltaTime;
    
    // Horizontal swipe: hide panel
    if (Math.abs(deltaX) > 80 && Math.abs(deltaX) > Math.abs(deltaY) && velocityX > 0.2) {
      if (deltaX < 0) {
        // Swipe left - hide
        setMobilePanelState('hidden');
      } else {
        // Swipe right - show
        setMobilePanelState('collapsed');
        setPanelHeight(MOBILE_COLLAPSED_HEIGHT);
      }
      return;
    }
    
    // Vertical: snap to closest state
    const fullHeight = window.innerHeight - 40; // near full-screen
    const midThreshold = window.innerHeight * 0.5;
    if (panelHeight < 140) {
      setPanelHeight(100);
      setMobilePanelState('collapsed');
    } else if (panelHeight > midThreshold) {
      setPanelHeight(fullHeight);
      setMobilePanelState('expanded');
    } else {
      // Snap to compact "header only" default
      setPanelHeight(MOBILE_COLLAPSED_HEIGHT);
      setMobilePanelState('collapsed');
    }
  };

  const showPanelFromHidden = () => {
    setMobilePanelState('collapsed');
    setPanelHeight(MOBILE_COLLAPSED_HEIGHT);
  };
  
  const theme = THEMES[section];
  const apiPath = section === "events" ? "events" : "markers";
  const entityLabel = section === "events" ? "evento" : "punto";
  const entityLabelPlural = section === "events" ? "eventos" : "puntos";
  
  // Use SWR for optimized data fetching with cache. When admin is logged in, request
  // hidden events too so they can be managed from the panel.
  const swrKey = section === "events"
    ? `${API}/events${user ? "?includeHidden=true" : ""}`
    : `${API}/${apiPath}`;
  const { data: markers = [], error, isLoading, mutate } = useSWR(swrKey, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    dedupingInterval: 2000,
  });
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMarker, setEditingMarker] = useState(null);
  const [newMarker, setNewMarker] = useState({
    lat: null,
    lng: null,
    name: "",
    description: "",
    date: "",
    time: "",
    recurrence: "once",
    weekdays: [],
    instagramUrl: ""
  });
  const [focusedPosition, setFocusedPosition] = useState(null);
  const [searchResult, setSearchResult] = useState(null); // { position: [lat, lng], label: string }
  const [cityFilter, setCityFilter] = useState("all"); // 'all' | 'neuquen' | 'cipolletti' | 'plottier'
  const [timeFilter, setTimeFilter] = useState("all"); // 'all' | 'today' | 'week' | 'month'
  const searchTimeoutRef = useRef(null);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setCheckingAuth(false);
    }
  }, []);

  const fetchPendingMarkers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/${apiPath}/pending`);
      setPendingMarkers(response.data);
    } catch (error) {
      if (isDev) console.error("Fetch pending error:", error);
      setPendingMarkers([]);
    }
  }, [apiPath]);

  // Check auth on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Auto-show help dialog on first visit (unless user opted out)
  useEffect(() => {
    const dismissed = localStorage.getItem("sbk_help_dismissed") === "true";
    if (!dismissed) {
      const timer = setTimeout(() => {
        setShowHelpDialog(true);
        setAutoHelpShown(true);
      }, 600);
      return () => clearTimeout(timer);
    }
  }, []);

  // Fetch pending markers when user logs in (admin only)
  useEffect(() => {
    if (user) {
      fetchPendingMarkers();
      // Poll every 10 seconds while logged in for near-realtime updates
      const interval = setInterval(fetchPendingMarkers, 10000);
      return () => clearInterval(interval);
    } else {
      setPendingMarkers([]);
    }
  }, [user, fetchPendingMarkers]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      sessionStorage.removeItem("sbk_access_token");
      setUser(null);
      toast.success("Sesión cerrada correctamente");
    } catch (error) {
      if (isDev) console.error("Logout error:", error);
      // Still clear local state even if network fails
      sessionStorage.removeItem("sbk_access_token");
      setUser(null);
      toast.error("Error al cerrar sesión");
    }
  };

  const handleMapClick = (latlng) => {
    if (markers.length >= MAX_MARKERS) {
      toast.error(`Máximo de ${MAX_MARKERS} ${entityLabelPlural} alcanzado`);
      return;
    }
    setNewMarker({
      lat: latlng.lat,
      lng: latlng.lng,
      name: "",
      description: "",
      date: "",
      time: "",
      recurrence: "once",
      weekdays: [],
      instagramUrl: ""
    });
    setEditingMarker(null);
    setIsDialogOpen(true);
  };

  const handleSaveMarker = async () => {
    try {
      if (!newMarker.name.trim()) {
        toast.error("El nombre es obligatorio");
        return;
      }
      if (section === "events") {
        if (!newMarker.time) {
          toast.error("Horario es obligatorio para eventos");
          return;
        }
        if (newMarker.recurrence === "once" && !newMarker.date) {
          toast.error("Fecha es obligatoria para eventos únicos");
          return;
        }
        if (newMarker.recurrence === "weekly" && (!Array.isArray(newMarker.weekdays) || newMarker.weekdays.length === 0)) {
          toast.error("Elegí al menos un día de la semana");
          return;
        }
      }

      const isEvent = section === "events";
      const payload = isEvent
        ? {
            lat: newMarker.lat,
            lng: newMarker.lng,
            name: newMarker.name,
            description: newMarker.description,
            date: newMarker.recurrence === "once" ? newMarker.date : null,
            time: newMarker.time,
            recurrence: newMarker.recurrence,
            weekdays: newMarker.recurrence === "weekly" ? newMarker.weekdays.map(Number) : null,
            instagramUrl: newMarker.instagramUrl,
          }
        : {
            lat: newMarker.lat,
            lng: newMarker.lng,
            name: newMarker.name,
            description: newMarker.description,
            instagramUrl: newMarker.instagramUrl,
          };

      if (editingMarker) {
        const updatePayload = isEvent
          ? {
              name: payload.name,
              description: payload.description,
              date: payload.date,
              time: payload.time,
              recurrence: payload.recurrence,
              weekdays: payload.weekdays,
              instagramUrl: payload.instagramUrl,
            }
          : { name: payload.name, description: payload.description, instagramUrl: payload.instagramUrl };
        await axios.put(`${API}/${apiPath}/${editingMarker.id}`, updatePayload);
        toast.success(`${entityLabel[0].toUpperCase() + entityLabel.slice(1)} actualizado exitosamente`);
      } else {
        await axios.post(`${API}/${apiPath}`, payload);
        if (isAuthenticated) {
          toast.success(`${entityLabel[0].toUpperCase() + entityLabel.slice(1)} enviado. Aprobalo desde la campanita.`);
        } else {
          toast.success(`${entityLabel[0].toUpperCase() + entityLabel.slice(1)} enviado. Aparecerá cuando el administrador lo apruebe.`);
        }
      }

      setIsDialogOpen(false);
      setNewMarker({ lat: null, lng: null, name: "", description: "", date: "", time: "", recurrence: "once", weekdays: [], instagramUrl: "" });
      setEditingMarker(null);
      mutate();
      // Refresh pending list if admin is logged in
      if (user) fetchPendingMarkers();
    } catch (error) {
      if (isDev) console.error("Error saving:", error);
      toast.error(error.response?.data?.detail || `Error al guardar el ${entityLabel}`);
    }
  };

  const handleDeleteMarker = async (markerId, e) => {
    e?.stopPropagation();
    
    try {
      await axios.delete(`${API}/${apiPath}/${markerId}`);
      toast.success(`${entityLabel[0].toUpperCase() + entityLabel.slice(1)} eliminado exitosamente`);
      mutate();
    } catch (error) {
      if (isDev) console.error("Error deleting:", error);
      toast.error(error.response?.data?.detail || `Error al eliminar el ${entityLabel}`);
    }
  };

  const handleToggleEventVisibility = async (eventId, currentlyHidden, e) => {
    e?.stopPropagation();
    try {
      await axios.post(`${API}/events/${eventId}/toggle-visibility`);
      toast.success(currentlyHidden ? "Evento visible" : "Evento oculto");
      mutate();
    } catch (error) {
      if (isDev) console.error("Error toggling visibility:", error);
      toast.error("No se pudo cambiar la visibilidad");
    }
  };


  const handleEditMarker = (marker, e) => {
    e?.stopPropagation();
    
    setNewMarker({
      lat: marker.lat,
      lng: marker.lng,
      name: marker.name,
      description: marker.description || "",
      date: marker.date || "",
      time: marker.time || "",
      recurrence: marker.recurrence || "once",
      weekdays: markerWeekdays(marker),
      instagramUrl: marker.instagramUrl || ""
    });
    setEditingMarker(marker);
    setIsDialogOpen(true);
  };

  const handleMarkerCardClick = (marker) => {
    setFocusedPosition([marker.lat, marker.lng]);
    // On mobile: hide the panel so the user can see the map and the pin with zoom
    if (isMobile) {
      setPanelHeight(MOBILE_COLLAPSED_HEIGHT);
      setMobilePanelState('collapsed');
    }
  };

  const handleShareMap = async () => {
    try {
      const url = window.location.href;
      
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success("Enlace copiado al portapapeles");
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = url;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
          toast.success("Enlace copiado al portapapeles");
        } catch (err) {
          toast.error("No se pudo copiar el enlace");
        }
        
        document.body.removeChild(textArea);
      }
    } catch (error) {
      if (isDev) console.error("Error copying to clipboard:", error);
      toast.error("No se pudo copiar el enlace");
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    try {
      // Smart search: detect city from query
      // Format: "address, city" -> uses specified city
      // Format: "address" (no comma) -> defaults to Neuquén
      const trimmed = searchQuery.trim();
      const parts = trimmed.split(",").map(p => p.trim()).filter(Boolean);
      
      let searchAddress, searchCity;
      if (parts.length >= 2) {
        // User specified a city
        searchAddress = parts.slice(0, -1).join(", ");
        const cityRaw = parts[parts.length - 1].toLowerCase();
        // Normalize common city aliases
        const cityMap = {
          "roca": "General Roca",
          "gral roca": "General Roca",
          "gral. roca": "General Roca",
          "general roca": "General Roca",
          "cipo": "Cipolletti",
          "cipolletti": "Cipolletti",
          "plottier": "Plottier",
          "neuquen": "Neuquén",
          "neuquén": "Neuquén",
          "nqn": "Neuquén",
          "allen": "Allen",
          "centenario": "Centenario",
          "cinco saltos": "Cinco Saltos",
          "fernandez oro": "General Fernández Oro",
          "fernández oro": "General Fernández Oro",
        };
        searchCity = cityMap[cityRaw] || parts[parts.length - 1];
      } else {
        // Default to Neuquén
        searchAddress = trimmed;
        searchCity = "Neuquén";
      }
      
      const fullQuery = `${searchAddress}, ${searchCity}, Rio Negro, Argentina`;
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullQuery)}&limit=1`
      );
      let data = await response.json();
      
      // Fallback: try without province if no results
      if (data.length === 0) {
        const fallbackQuery = `${searchAddress}, ${searchCity}, Argentina`;
        const fallbackResp = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fallbackQuery)}&limit=1`
        );
        data = await fallbackResp.json();
      }
      
      if (data.length > 0) {
        const { lat, lon, display_name } = data[0];
        const position = [parseFloat(lat), parseFloat(lon)];
        
        setFocusedPosition(position);
        setSearchResult({ position, label: display_name });
        
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
          setSearchResult(null);
        }, 20000);
        
        toast.success(`Ubicación encontrada en ${searchCity}`);
      } else {
        toast.error("No se encontró la ubicación");
      }
    } catch (error) {
      if (isDev) console.error("Search error:", error);
      toast.error("Error al buscar la ubicación");
    } finally {
      setIsSearching(false);
    }
  };

  const dismissSearchResult = useCallback(() => {
    setSearchResult(null);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
  }, []);

  const isAuthenticated = user !== null;

  // Memoize marker positions to avoid recreating arrays on every render
  const markerPositions = useMemo(
    () => markers.map(m => ({ id: m.id, position: [m.lat, m.lng], data: m })),
    [markers]
  );

  // Filter markers by selected city (applies to both list and map pins). For the events
  // section we also apply a time-window filter (Hoy/Semana/Mes/Todos) and sort by
  // "next occurrence" ascending so the soonest events land on top.
  const filteredMarkers = useMemo(() => {
    let base = cityFilter === "all" ? markers : markers.filter((m) => cityOfMarker(m) === cityFilter);
    if (section !== "events") return base;

    // Apply time-window filter using the computed next occurrence timestamp
    if (timeFilter !== "all") {
      const now = new Date();
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
      let cutoff;
      if (timeFilter === "today") {
        cutoff = startOfToday + 24 * 60 * 60 * 1000; // end of today
      } else if (timeFilter === "week") {
        // Until end of Sunday of the current week (inclusive). After Sunday passes, the
        // window rolls forward to the next Sunday automatically because getDay() updates.
        const daysUntilSunday = (7 - now.getDay()) % 7; // 0 when today IS Sunday
        cutoff = startOfToday + (daysUntilSunday + 1) * 24 * 60 * 60 * 1000; // include full Sunday
      } else if (timeFilter === "month") {
        cutoff = startOfToday + 30 * 24 * 60 * 60 * 1000; // rolling 30 days
      } else {
        cutoff = Number.POSITIVE_INFINITY;
      }
      base = base.filter((m) => {
        const ts = eventNextTimestamp(m);
        return ts >= startOfToday && ts < cutoff;
      });
    }

    // Sort a shallow copy so we don't mutate SWR data
    return [...base].sort((a, b) => eventNextTimestamp(a) - eventNextTimestamp(b));
  }, [markers, cityFilter, section, timeFilter]);

  // Compute dynamic list of cities that actually have at least one marker (per current section).
  // Preserves the CITIES order and appends "Otras" bucket last if any marker falls outside.
  const availableCities = useMemo(() => {
    const counts = {};
    for (const m of markers) {
      const cid = cityOfMarker(m);
      counts[cid] = (counts[cid] || 0) + 1;
    }
    const list = CITIES.filter((c) => counts[c.id]).map((c) => ({ ...c, count: counts[c.id] }));
    if (counts["otra"]) {
      list.push({ id: "otra", label: "Otras", count: counts["otra"] });
    }
    return list;
  }, [markers]);

  // Reset filter if the current selection no longer has matching markers (e.g. section switch)
  useEffect(() => {
    if (cityFilter !== "all" && !availableCities.some((c) => c.id === cityFilter)) {
      setCityFilter("all");
    }
  }, [availableCities, cityFilter]);

  // Reset time filter when leaving the events section
  useEffect(() => {
    if (section !== "events" && timeFilter !== "all") {
      setTimeFilter("all");
    }
  }, [section, timeFilter]);

  const filteredMarkerPositions = useMemo(
    () => filteredMarkers.map(m => ({ id: m.id, position: [m.lat, m.lng], data: m })),
    [filteredMarkers]
  );

  // Memoize search marker event handlers to avoid inline object re-creation
  const searchMarkerHandlers = useMemo(
    () => ({ click: dismissSearchResult }),
    [dismissSearchResult]
  );

  return (
    <div className="relative w-screen h-screen overflow-hidden">
      <MapContainer
        center={MAP_CENTER}
        zoom={MAP_ZOOM}
        className="w-full h-full z-0"
        zoomControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://carto.com/">CARTO</a>'
          url="https://basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png?key=cb1_28yy_1_a4fc58524fbed9e4e141ad69"
        />

        {/* Zoom controls - visible top-right, touch-friendly on mobile */}
        <ZoomControl position="topright" />

        {filteredMarkerPositions.map(({ id, position, data: marker }) => (
          <Marker
            key={id}
            position={position}
            icon={createCustomIcon(theme.primary)}
          >
            <Popup>
              <div className="text-sm">
                <h3 className="font-semibold text-base mb-1">{marker.name}</h3>
                {section === "events" && (marker.date || marker.time || marker.recurrence === "weekly") && (
                  <div
                    className="flex items-center gap-1.5 mb-2 text-xs font-semibold flex-wrap"
                    style={{ color: theme.primary }}
                  >
                    <Calendar size={13} />
                    {marker.recurrence === "weekly" ? (
                      <span>
                        {formatWeekdaysLabel(markerWeekdays(marker))}
                        <span className="text-slate-400 font-normal ml-1">
                          · próx. {formatFriendlyDate(nextDateForWeekdays(markerWeekdays(marker)))}
                        </span>
                      </span>
                    ) : (
                      <span>{formatFriendlyDate(marker.date)}</span>
                    )}
                    {marker.time && (
                      <>
                        <span className="text-slate-300">•</span>
                        <Clock size={13} />
                        <span>{marker.time}</span>
                      </>
                    )}
                  </div>
                )}
                {marker.description && (
                  <p className="text-slate-600 mb-2">{marker.description}</p>
                )}
                {marker.instagramUrl && (
                  <a
                    href={marker.instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 hover:underline"
                    style={{ color: theme.primary }}
                  >
                    <Instagram size={14} />
                    Ver Instagram
                  </a>
                )}

                {section === "events" && isMobile && (
                  <div className="mt-2">
                    <a
                      href={gmapsDirUrl(marker.lat, marker.lng)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-white rounded-full px-3 py-1.5"
                      style={{ backgroundColor: theme.primary }}
                    >
                      <Navigation size={13} />
                      Cómo llegar
                    </a>
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
        ))}

        <MapClickHandler onMapClick={handleMapClick} disabled={markers.length >= MAX_MARKERS} />
        {focusedPosition && <FlyToMarker position={focusedPosition} zoom={searchResult ? 17 : 18} />}
        
        {/* Green temporary marker for search results */}
        {searchResult && (
          <Marker
            position={searchResult.position}
            icon={createSearchIcon()}
            eventHandlers={searchMarkerHandlers}
          >
            <Popup>
              <div className="text-sm">
                <div className="flex items-center gap-1.5 mb-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <h3 className="font-semibold text-base text-green-700">Resultado de búsqueda</h3>
                </div>
                <p className="text-slate-600 text-xs mb-2 max-w-[240px]">{searchResult.label}</p>
                <button
                  onClick={dismissSearchResult}
                  className="text-xs text-slate-500 hover:text-slate-700 underline"
                >
                  Ocultar marcador
                </button>
              </div>
            </Popup>
          </Marker>
        )}
      </MapContainer>

      {/* Search Bar */}
      <div className="absolute top-4 md:top-6 left-1/2 transform -translate-x-1/2 z-20 w-[calc(100%-2rem)] sm:w-[90%] md:w-[500px] max-w-[500px]">
        <form onSubmit={handleSearch} className="glass-panel p-2 md:p-3 flex gap-2">
          <Input
            type="text"
            placeholder="Ej: Av. Argentina 100  |  25 de mayo 200, Roca"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 rounded-xl border-slate-200 text-sm md:text-base h-9 md:h-10"
          />
          <Button
            type="submit"
            disabled={isSearching}
            className="bg-[#FF5A5F] hover:bg-[#E0484D] text-white rounded-xl h-9 md:h-10 px-3 md:px-4"
          >
            <Search size={18} />
          </Button>
        </form>
      </div>

      {/* Panel Content (reused for both mobile and desktop) */}
      {(() => {
        // Left column content: logo + tabs + auth + stats + share button
        const leftPanelContent = (
          <>
            {/* Header Card */}
            <div className="glass-panel p-3 md:p-4">
              <img
                src="https://customer-assets-39nsmqrw.emergentagent.net/job_neuquen-mapa-puntos/artifacts/gqry3osg_logo%20pagina.jpg"
                alt="SBK-MAPS - Mapa de escuelas de Salsa, Bachata y Kizomba"
                className="w-full max-h-[70px] md:max-h-[170px] object-contain rounded-lg mb-3"
              />

              {/* Section Tabs */}
              <div className="flex gap-1 bg-slate-100 rounded-full p-1 mb-3" role="tablist">
                <button
                  data-testid="tab-schools"
                  role="tab"
                  aria-selected={section === "schools"}
                  onClick={() => setSection("schools")}
                  className={`flex-1 py-2 px-3 rounded-full text-xs md:text-sm font-semibold transition-all ${
                    section === "schools" ? THEMES.schools.tabActive : THEMES.schools.tabInactive
                  }`}
                >
                  🥁 Escuelas
                </button>
                <button
                  data-testid="tab-events"
                  role="tab"
                  aria-selected={section === "events"}
                  onClick={() => setSection("events")}
                  className={`flex-1 py-2 px-3 rounded-full text-xs md:text-sm font-semibold transition-all ${
                    section === "events" ? THEMES.events.tabActive : THEMES.events.tabInactive
                  }`}
                >
                  🎉 Eventos
                </button>
              </div>
              
              <div className="flex gap-2 mb-3">
                {isAuthenticated ? (
                  <Button
                    data-testid="logout-btn"
                    onClick={handleLogout}
                    size="sm"
                    variant="outline"
                    className="flex-1 h-10 rounded-full font-semibold text-sm"
                  >
                    <LogOut size={16} className="mr-1.5" />
                    Salir
                  </Button>
                ) : (
                  <Button
                    data-testid="login-btn"
                    onClick={() => setShowLoginDialog(true)}
                    size="sm"
                    className="flex-1 h-10 text-white rounded-full font-semibold text-sm shadow-md"
                    style={{ backgroundColor: theme.primary, boxShadow: `0 4px 12px ${theme.primary}44` }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = theme.primaryHover)}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = theme.primary)}
                  >
                    <LogIn size={16} className="mr-1.5" />
                    Admin
                  </Button>
                )}
                {isAuthenticated && (
                  <Button
                    data-testid="notifications-btn"
                    onClick={() => setShowPendingDialog(true)}
                    size="sm"
                    variant="outline"
                    className="h-10 w-10 p-0 rounded-full relative shrink-0"
                  >
                    <Bell size={18} className="text-slate-700" />
                    {pendingMarkers.length > 0 && (
                      <span
                        data-testid="notifications-badge"
                        className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse"
                        style={{ backgroundColor: theme.primary }}
                      >
                        {pendingMarkers.length}
                      </span>
                    )}
                  </Button>
                )}
              </div>
              
              {isAuthenticated && (
                <div className="flex items-center gap-2 mb-3 p-2 bg-blue-50 rounded-lg">
                  <UserIcon size={16} className="text-blue-600" />
                  <span className="text-sm font-medium text-blue-900">{user.username}</span>
                </div>
              )}
              
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-600">
                    {entityLabelPlural} aprobados
                  </span>
                  <span className="text-sm font-semibold text-slate-900">
                    {markers.length}
                  </span>
                </div>
                {isAuthenticated && pendingMarkers.length > 0 && (
                  <div className="flex justify-between items-center pt-1">
                    <span className="text-xs font-bold uppercase tracking-wider text-orange-600">
                      En espera
                    </span>
                    <span className="text-sm font-semibold text-orange-700">
                      {pendingMarkers.length}/{MAX_PENDING}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Share Button - always highlighted */}
            <Button
              data-testid="share-map-btn"
              onClick={handleShareMap}
              className="glass-panel w-full py-4 md:py-6 text-white rounded-full font-semibold text-sm md:text-base hover:-translate-y-0.5 transition-all duration-300"
              style={{
                backgroundColor: theme.primary,
                boxShadow: `0 8px 20px ${theme.primary}66`,
              }}
            >
              <Share2 size={18} className="mr-2" />
              Compartir Mapa
            </Button>
          </>
        );

        // Right column content: markers list only
        const rightPanelContent = (
          <div className={`glass-panel flex flex-col ${isMobile ? "" : "flex-1 overflow-hidden min-h-0"}`}>
            <div className="px-3 md:px-4 pt-3 md:pt-4 pb-2 border-b border-white/40">
              <div className="flex items-center justify-between gap-2 mb-2">
                <h2 className="text-base md:text-lg font-semibold tracking-tight text-slate-900">
                  {section === "events" ? "Próximos eventos" : "Puntos guardados"}
                </h2>
                {availableCities.length > 0 && (
                  <select
                    data-testid="city-filter-select"
                    value={cityFilter}
                    onChange={(e) => setCityFilter(e.target.value)}
                    className="text-xs font-semibold rounded-full px-3 py-1.5 border border-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-2"
                    style={{ ["--tw-ring-color"]: theme.primary + "40" }}
                    aria-label="Filtrar por ciudad"
                  >
                    <option value="all">Todas ({markers.length})</option>
                    {availableCities.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label} ({c.count})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Time-window chips: only relevant for events */}
              {section === "events" && (
                <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filtrar por rango de tiempo">
                  {[
                    { id: "today", label: "Hoy" },
                    { id: "week", label: "Semana" },
                    { id: "month", label: "Mes" },
                    { id: "all", label: "Todos" },
                  ].map((opt) => {
                    const active = timeFilter === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        data-testid={`time-filter-${opt.id}`}
                        onClick={() => setTimeFilter(opt.id)}
                        className={`text-xs font-semibold rounded-full px-3 py-1 border transition-all ${
                          active
                            ? "text-white border-transparent"
                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                        }`}
                        style={active ? { backgroundColor: theme.primary } : undefined}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {(() => {
              const listBody = isLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="p-4 bg-white/50 rounded-xl border border-white/60">
                      <Skeleton className="h-5 w-3/4 mb-2" />
                      <Skeleton className="h-4 w-full mb-2" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  ))}
                </div>
              ) : filteredMarkers.length === 0 ? (
                <div className="text-center py-12">
                  <MapPin className="mx-auto mb-3 text-slate-300" size={48} />
                  <p className="text-sm text-slate-500">
                    {markers.length === 0
                      ? `Haz doble clic en el mapa para agregar ${entityLabelPlural}`
                      : section === "events" && timeFilter !== "all"
                      ? "No hay eventos en este rango"
                      : `No hay ${entityLabelPlural} en esta ciudad`}
                  </p>
                </div>
              ) : (
                <div
                  className="space-y-3"
                  data-testid={isMobile ? "mobile-card-list" : "cards-vertical-list"}
                >
                  {filteredMarkers.map((marker) => (
                    <div
                      key={marker.id}
                      data-testid={`marker-card-${marker.id}`}
                      className={`marker-card bg-white rounded-xl border border-slate-200 hover:shadow-lg transition-all duration-300 ${marker.hidden ? "opacity-60 ring-2 ring-amber-200" : ""}`}
                    >
                      <div
                        className="p-4 cursor-pointer"
                        onClick={() => handleMarkerCardClick(marker)}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold text-slate-900 text-base flex-1 pr-2">
                            {marker.name}
                          </h3>
                        </div>

                        {section === "events" && (marker.date || marker.time || marker.recurrence === "weekly") && (
                          <div className="flex items-center gap-2 mb-2 text-xs font-semibold flex-wrap" style={{ color: theme.primary }}>
                            <Calendar size={13} />
                            {marker.recurrence === "weekly" ? (
                              <span>
                                {formatWeekdaysLabel(markerWeekdays(marker))}
                                <span className="text-slate-400 font-normal ml-1">
                                  · próx. {formatFriendlyDate(nextDateForWeekdays(markerWeekdays(marker)))}
                                </span>
                              </span>
                            ) : (
                              <span>{formatFriendlyDate(marker.date)}</span>
                            )}
                            {marker.time && (
                              <>
                                <span className="text-slate-300">•</span>
                                <Clock size={13} />
                                <span>{marker.time}</span>
                              </>
                            )}
                          </div>
                        )}

                        {marker.description && (
                          <p className="text-sm text-slate-600 mb-3 leading-relaxed">
                            {marker.description}
                          </p>
                        )}

                        {marker.instagramUrl && (
                          <a
                            href={marker.instagramUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1.5 text-sm transition-colors font-medium"
                            style={{ color: theme.primary }}
                          >
                            <Instagram size={16} />
                            Ver perfil
                          </a>
                        )}

                        {section === "events" && isMobile && (
                          <div className="mt-2">
                            <a
                              href={gmapsDirUrl(marker.lat, marker.lng)}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`nav-btn-${marker.id}`}
                              className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-white rounded-full px-3 py-1.5 shadow-sm active:scale-95 transition-transform"
                              style={{ backgroundColor: theme.primary }}
                            >
                              <Navigation size={14} />
                              Cómo llegar
                            </a>
                          </div>
                        )}
                      </div>

                      {isAuthenticated && (
                        <div className="flex flex-wrap gap-2 px-4 pb-4 pt-2 border-t border-slate-100">
                          <Button
                            data-testid={`edit-marker-${marker.id}`}
                            onClick={(e) => handleEditMarker(marker, e)}
                            size="sm"
                            className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-700 border-none shadow-none h-9"
                          >
                            <Pencil size={16} className="mr-2" />
                            Editar
                          </Button>
                          {section === "events" && (
                            <Button
                              data-testid={`toggle-visibility-${marker.id}`}
                              onClick={(e) => handleToggleEventVisibility(marker.id, !!marker.hidden, e)}
                              size="sm"
                              variant="outline"
                              className={`flex-1 h-9 border-slate-200 ${marker.hidden ? "bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-200" : "bg-slate-50 hover:bg-slate-100 text-slate-700"}`}
                            >
                              {marker.hidden ? <EyeOff size={16} className="mr-2" /> : <Eye size={16} className="mr-2" />}
                              {marker.hidden ? "Oculto" : "Ocultar"}
                            </Button>
                          )}
                          <Button
                            data-testid={`delete-marker-${marker.id}`}
                            onClick={(e) => handleDeleteMarker(marker.id, e)}
                            size="sm"
                            variant="outline"
                            className="flex-1 bg-red-50 hover:bg-red-100 text-red-600 border-red-200 hover:border-red-300 h-9"
                          >
                            <Trash2 size={16} className="mr-2" />
                            Eliminar
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );

              // Mobile: no internal scroll (outer panel container scrolls vertically).
              // Desktop: keep ScrollArea for pretty vertical scrollbar.
              if (isMobile) {
                return (
                  <div
                    className="p-3"
                    data-testid="mobile-list-container"
                  >
                    {listBody}
                  </div>
                );
              }
              return (
                <ScrollArea className="flex-1 p-3 md:p-4">
                  {listBody}
                </ScrollArea>
              );
            })()}
          </div>
        );

        // For mobile bottom sheet: keep everything stacked together
        const mobilePanelContent = (
          <>
            {leftPanelContent}
            {rightPanelContent}
          </>
        );

        if (isMobile) {
          const isPanelExpanded = panelHeight > 260;
          const togglePanelSize = () => {
            if (panelHeight > 260) {
              setPanelHeight(MOBILE_COLLAPSED_HEIGHT);
              setMobilePanelState('collapsed');
            } else {
              setPanelHeight(window.innerHeight - 40);
              setMobilePanelState('expanded');
            }
          };
          return (
            <>
              {/* Chevron toggle: fixed with high z-index so it stays above the search bar even
                  when the panel is expanded and overlaps the top of the screen. */}
              {mobilePanelState !== 'hidden' && (
                <button
                  type="button"
                  data-testid="mobile-panel-toggle"
                  onClick={togglePanelSize}
                  aria-label={isPanelExpanded ? "Colapsar panel" : "Expandir panel a pantalla completa"}
                  className="fixed right-2 z-30 w-9 h-9 rounded-full flex items-center justify-center bg-white/95 border border-slate-200 shadow-md active:scale-95 transition-transform"
                  style={{
                    bottom: `${panelHeight - 4}px`,
                    transition: isDragging ? 'none' : 'bottom 0.25s ease-out',
                  }}
                >
                  {isPanelExpanded ? (
                    <ChevronDown size={20} className="text-slate-700" />
                  ) : (
                    <ChevronUp size={20} className="text-slate-700" />
                  )}
                </button>
              )}

              {/* Mobile Bottom Sheet Panel */}
              <div
                data-testid="mobile-panel"
                className={`fixed left-0 right-0 bottom-0 z-10 flex flex-col`}
                style={{
                  height: `${panelHeight}px`,
                  transform: mobilePanelState === 'hidden' ? 'translateX(-100%)' : 'translateX(0)',
                  transition: isDragging ? 'none' : 'height 0.25s ease-out, transform 0.3s ease-out',
                }}
              >
                {/* Drag handle */}
                <div
                  className="glass-panel rounded-t-2xl rounded-b-none pt-2 pb-2 flex flex-col items-center cursor-grab active:cursor-grabbing"
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  style={{ touchAction: 'none' }}
                >
                  <div className="w-12 h-1.5 bg-slate-300 rounded-full"></div>
                </div>

                {/* Panel content area (native vertical scroll so user can browse content
                    without having to enlarge the panel manually) */}
                <div className="glass-panel rounded-t-none rounded-b-none flex-1 flex flex-col overflow-y-auto overflow-x-hidden px-3 pb-3 pt-2 gap-3 border-t-0" style={{ touchAction: 'pan-y' }}>
                  {mobilePanelContent}
                </div>
              </div>

              {/* Show button when hidden (swipe right area) */}
              {mobilePanelState === 'hidden' && (
                <button
                  data-testid="show-mobile-panel-btn"
                  onClick={showPanelFromHidden}
                  onTouchStart={(e) => {
                    dragStartRef.current = {
                      y: e.touches[0].clientY,
                      x: e.touches[0].clientX,
                      height: panelHeight,
                      time: Date.now(),
                    };
                  }}
                  onTouchEnd={(e) => {
                    const deltaX = e.changedTouches[0].clientX - dragStartRef.current.x;
                    if (deltaX > 40) {
                      showPanelFromHidden();
                    }
                  }}
                  className="fixed left-0 bottom-24 z-10 glass-panel w-10 h-16 rounded-r-2xl rounded-l-none flex items-center justify-center animate-pulse"
                  aria-label="Mostrar panel"
                >
                  <ChevronRight size={22} className="text-[#FF5A5F]" />
                </button>
              )}
            </>
          );
        }

        // Desktop layout: LEFT (unchanged 380px logo/tabs/auth/share) + RIGHT (wider 570px full-height data list)
        return (
          <>
            {/* Left column - fixed width, in original position and size */}
            <div className="absolute left-4 top-4 bottom-4 w-[380px] z-10 flex flex-col gap-6 min-h-0">
              {leftPanelContent}
            </div>

            {/* Right column - 50% wider (570px), extends top to bottom, collapsible */}
            <div
              className={`absolute right-4 top-4 bottom-4 z-10 flex gap-3 transition-all duration-300 ${
                isPanelCollapsed ? 'w-12' : 'w-[570px]'
              }`}
            >
              <button
                onClick={() => setIsPanelCollapsed(!isPanelCollapsed)}
                className="glass-panel w-12 h-12 flex items-center justify-center rounded-full hover:bg-white/90 transition-all self-center shrink-0"
                aria-label={isPanelCollapsed ? "Expandir panel de datos" : "Colapsar panel de datos"}
                data-testid="toggle-data-panel"
              >
                {isPanelCollapsed ? (
                  <ChevronLeft size={24} className="text-slate-700" />
                ) : (
                  <ChevronRight size={24} className="text-slate-700" />
                )}
              </button>
              {!isPanelCollapsed && (
                <div className="flex-1 flex flex-col min-h-0">
                  {rightPanelContent}
                </div>
              )}
            </div>
          </>
        );
      })()}

      {/* Floating Help Button - Bottom Right */}
      <button
        data-testid="help-btn"
        onClick={() => setShowHelpDialog(true)}
        className="fixed bottom-4 md:bottom-6 right-4 md:right-6 z-20 w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/90 backdrop-blur-xl border border-white/60 shadow-lg hover:shadow-xl hover:-translate-y-0.5 flex items-center justify-center transition-all duration-300"
        aria-label="Ayuda"
      >
        <HelpCircle size={22} style={{ color: theme.primary }} />
      </button>

      {/* Add/Edit Marker Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingMarker
                ? `Editar ${entityLabel}`
                : `Agregar ${entityLabel}`}
            </DialogTitle>
            <DialogDescription>
              {editingMarker
                ? `Modifica la información del ${entityLabel}`
                : `Completa la información del nuevo ${entityLabel}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre *</Label>
              <Input
                id="name"
                data-testid="marker-name-input"
                placeholder={section === "events" ? "Ej: Milonga con Los Herederos" : "Ej: Escuela de Salsa La Havana"}
                value={newMarker.name}
                onChange={(e) => setNewMarker({ ...newMarker, name: e.target.value })}
                className="rounded-xl"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                data-testid="marker-description-input"
                placeholder={section === "events" ? "Detalles del evento, precio, dresscode..." : "Estilos, niveles, horarios..."}
                value={newMarker.description}
                onChange={(e) => setNewMarker({ ...newMarker, description: e.target.value })}
                className="rounded-xl resize-none"
                rows={3}
              />
            </div>

            {section === "events" && (
              <>
                <div className="space-y-2">
                  <Label>Tipo de evento *</Label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      data-testid="recurrence-once-btn"
                      onClick={() => setNewMarker({ ...newMarker, recurrence: "once", weekday: null })}
                      className={`flex-1 py-2 px-3 rounded-xl text-sm font-semibold border transition-all ${
                        newMarker.recurrence === "once"
                          ? "text-white border-transparent"
                          : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                      }`}
                      style={newMarker.recurrence === "once" ? { backgroundColor: theme.primary } : undefined}
                    >
                      📅 Fecha única
                    </button>
                    <button
                      type="button"
                      data-testid="recurrence-weekly-btn"
                      onClick={() => setNewMarker({ ...newMarker, recurrence: "weekly", date: "" })}
                      className={`flex-1 py-2 px-3 rounded-xl text-sm font-semibold border transition-all ${
                        newMarker.recurrence === "weekly"
                          ? "text-white border-transparent"
                          : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                      }`}
                      style={newMarker.recurrence === "weekly" ? { backgroundColor: theme.primary } : undefined}
                    >
                      🔁 Todas las semanas
                    </button>
                  </div>
                </div>

                {newMarker.recurrence === "once" ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="event-date">Fecha *</Label>
                      <Input
                        id="event-date"
                        data-testid="marker-date-input"
                        type="date"
                        value={newMarker.date}
                        onChange={(e) => setNewMarker({ ...newMarker, date: e.target.value })}
                        min={new Date().toISOString().split("T")[0]}
                        className="rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="event-time">Horario *</Label>
                      <Input
                        id="event-time"
                        data-testid="marker-time-input"
                        type="time"
                        value={newMarker.time}
                        onChange={(e) => setNewMarker({ ...newMarker, time: e.target.value })}
                        className="rounded-xl"
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label>Días de la semana * (podés elegir varios)</Label>
                      <div className="grid grid-cols-4 gap-1.5">
                        {WEEKDAYS.map((d) => {
                          const active = (newMarker.weekdays || []).includes(d.value);
                          return (
                            <button
                              key={d.value}
                              type="button"
                              data-testid={`marker-weekday-${d.value}`}
                              onClick={() => {
                                const cur = new Set(newMarker.weekdays || []);
                                if (cur.has(d.value)) cur.delete(d.value); else cur.add(d.value);
                                setNewMarker({ ...newMarker, weekdays: Array.from(cur).sort((a, b) => a - b) });
                              }}
                              className={`text-xs font-semibold rounded-xl py-2 border transition-all ${
                                active
                                  ? "text-white border-transparent"
                                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                              }`}
                              style={active ? { backgroundColor: theme.primary } : undefined}
                            >
                              {d.label.slice(0, 3)}
                            </button>
                          );
                        })}
                      </div>
                      {(newMarker.weekdays || []).length > 0 && (
                        <p className="text-xs text-slate-500">
                          {formatWeekdaysLabel(newMarker.weekdays)}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="event-time-weekly">Horario *</Label>
                      <Input
                        id="event-time-weekly"
                        data-testid="marker-time-input"
                        type="time"
                        value={newMarker.time}
                        onChange={(e) => setNewMarker({ ...newMarker, time: e.target.value })}
                        className="rounded-xl"
                      />
                    </div>
                  </>
                )}
              </>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="instagram">Link de Instagram</Label>
              <Input
                id="instagram"
                data-testid="marker-instagram-input"
                placeholder="https://instagram.com/..."
                value={newMarker.instagramUrl}
                onChange={(e) => setNewMarker({ ...newMarker, instagramUrl: e.target.value })}
                className="rounded-xl"
              />
            </div>
          </div>
          
          <div className="flex gap-3">
            <Button
              data-testid="cancel-marker-btn"
              variant="outline"
              onClick={() => {
                setIsDialogOpen(false);
                setNewMarker({ lat: null, lng: null, name: "", description: "", date: "", time: "", recurrence: "once", weekdays: [], instagramUrl: "" });
                setEditingMarker(null);
              }}
              className="flex-1 rounded-full"
            >
              Cancelar
            </Button>
            <Button
              data-testid="save-marker-btn"
              onClick={handleSaveMarker}
              className="flex-1 text-white rounded-full"
              style={{ backgroundColor: theme.primary }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = theme.primaryHover)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = theme.primary)}
            >
              {editingMarker ? "Actualizar" : "Agregar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Login Dialog */}
      <LoginDialog
        open={showLoginDialog}
        onClose={() => setShowLoginDialog(false)}
        onLoginSuccess={(userData) => setUser(userData)}
      />

      {/* Help Dialog */}
      <HelpDialog
        open={showHelpDialog}
        onClose={() => {
          setShowHelpDialog(false);
          setAutoHelpShown(false);
        }}
        showRememberOption={autoHelpShown}
      />

      {/* Pending Approvals Dialog - Admin only */}
      <PendingApprovalsDialog
        open={showPendingDialog}
        onClose={() => setShowPendingDialog(false)}
        pendingMarkers={pendingMarkers}
        apiPath={apiPath}
        entityLabel={entityLabel}
        onAction={() => {
          fetchPendingMarkers();
          mutate();
        }}
      />
    </div>
  );
}

export default App;
